import { Response, Currency, apiAbortController, ChangeLog } from '@ui/src';
import api from '../../../api/api';
import {
  TransactionsTypesRiskScore,
  TransactionVolume,
  TransactionVolumeFormValues,
} from '../../types';

export const transactionsRiskApi = api.injectEndpoints({
  endpoints: (build) => ({
    getTransactionVolumes: build.query<Response<TransactionVolume>, void>({
      query: () => ({
        url: '/v1/transactions-volumes?all=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAmlBaseCurrency: build.query<{ data: Currency }, void>({
      query: () => ({
        url: '/v1/currencies/base',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createTransactionVolume: build.mutation<
      {
        data: TransactionVolumeFormValues;
      },
      TransactionVolumeFormValues
    >({
      query: (body: TransactionVolumeFormValues) => ({
        url: '/v1/transactions-volumes',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateTransactionVolumeRisk: build.mutation<
      {
        data: TransactionVolumeFormValues;
      },
      { body: TransactionVolumeFormValues; id: string }
    >({
      query: (data) => ({
        url: `/v1/transactions-volumes/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTransactionTypesRisk: build.query<
      Response<TransactionsTypesRiskScore>,
      string
    >({
      query: (query) => ({
        url: `/v1/transactions-types-scores${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTransactionTypeRisk: build.query<
      { data: TransactionsTypesRiskScore },
      string
    >({
      query: (id) => ({
        url: `/v1/transactions-types-scores/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createTransactionTypesRisk: build.mutation<
      {
        data: TransactionsTypesRiskScore;
      },
      {
        paymentType?: string;
        riskScore: number;
        transactionTypeId: string;
      }
    >({
      query: (body) => ({
        url: '/v1/transactions-types-scores',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateTransactionTypesRisk: build.mutation<
      {
        data: TransactionsTypesRiskScore;
      },
      {
        body: {
          paymentType?: string;
          riskScore: number;
          transactionTypeId: string;
        };
        id: string;
      }
    >({
      query: (data) => ({
        url: `/v1/transactions-types-scores/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTransactionTypesRiskChangeLogs: build.query<Response<ChangeLog>, string>(
      {
        query: (query) => ({
          url: `/v1/audit-log${query}`,
          signal: apiAbortController.getSignalToken(),
        }),
      },
    ),
  }),
});

export const {
  useLazyGetTransactionVolumesQuery,
  useLazyGetAmlBaseCurrencyQuery,
  useCreateTransactionVolumeMutation,
  useUpdateTransactionVolumeRiskMutation,
  useLazyGetTransactionTypesRiskQuery,
  useCreateTransactionTypesRiskMutation,
  useLazyGetTransactionTypeRiskQuery,
  useUpdateTransactionTypesRiskMutation,
  useLazyGetTransactionTypesRiskChangeLogsQuery,
} = transactionsRiskApi;

export const {
  endpoints: {
    getTransactionVolumes,
    getAmlBaseCurrency,
    createTransactionVolume,
    updateTransactionVolumeRisk,
    getTransactionTypesRisk,
    createTransactionTypesRisk,
    getTransactionTypeRisk,
    updateTransactionTypesRisk,
    getTransactionTypesRiskChangeLogs,
  },
} = transactionsRiskApi;
